/**
 * ACTIVITY-CORE — "History" sheet for entity detail (recipe):
 * local history of that entity with "Undo this change" per entry. Base
 * Dialog already integrates useBackDismiss (back dismisses overlay).
 */
import { msg } from "@lingui/core/macro";
import { History } from "@fleet/icons";
import { type ActivityRow, getEntityActivityQuery, queryKeys } from "@mealplan/shared";
import { createPowerSyncQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ActivityList } from "./activity-list";
import { useActivityNameResolver } from "../lib/use-activity-names";

const ENTITY_HISTORY_LIMIT = 100;

interface EntityHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "recipe" | "shopping_item" | "pantry_item" | "meal_plan_entry";
  entityId: string;
}

export function EntityHistoryDialog(props: EntityHistoryDialogProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const resolveName = useActivityNameResolver({ db, drizzle, userId });

  const historyQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.entity(userId(), props.entityType, props.entityId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getEntityActivityQuery(dbInstance, props.entityType, props.entityId, {
            limit: ENTITY_HISTORY_LIMIT,
          })
        : null;
    },
    enabled: () => props.open && Boolean(drizzle()),
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogHeader>
        <DialogTitle>
          <span class="flex items-center gap-2">
            <History class="h-5 w-5 text-primary" />
            {t(msg`History`)}
          </span>
        </DialogTitle>
      </DialogHeader>
      <div class="flex max-h-[70vh] flex-col gap-3" data-testid="entity-history">
        <p class="text-xs text-muted-foreground">
          {t(msg`Every change on this item, newest first.`)}
        </p>
        <div class="min-h-0 overflow-y-auto pr-1">
          <ActivityList
            rows={() => historyQuery.data ?? []}
            isLoading={() => historyQuery.isFetching && (historyQuery.data ?? []).length === 0}
            currentUserId={userId}
            canRevert
            resolveName={resolveName}
          />
        </div>
      </div>
    </Dialog>
  );
}
